/**
 * JSONValidator Service
 * 
 * Provides JSON validation functionality with detailed error reporting
 * including line and column information for syntax errors.
 */

export class JSONValidator {
    /**
     * Validates a JSON string and returns validation result
     * @param {string} jsonString - The JSON string to validate
     * @returns {ValidationResult} Object containing validation status and details
     */
    static validate(jsonString) {
        // Handle empty or null input
        if (!jsonString || typeof jsonString !== 'string') {
            return {
                isValid: false,
                error: 'Input is empty or not a string',
                errorType: 'EMPTY_INPUT',
                line: null,
                column: null,
                message: 'Please provide a valid JSON string'
            }
        }

        // Trim whitespace for validation
        const trimmedInput = jsonString.trim()
        
        if (trimmedInput === '') {
            return {
                isValid: false,
                error: 'Input is empty',
                errorType: 'EMPTY_INPUT',
                line: null,
                column: null,
                message: 'Please provide a valid JSON string'
            }
        }

        try {
            // Attempt to parse the JSON
            const parsed = JSON.parse(jsonString)
            
            return {
                isValid: true,
                data: parsed,
                error: null,
                errorType: null,
                line: null,
                column: null,
                message: 'Valid JSON'
            }
        } catch (error) {
            // Parse the error to extract line and column information
            const errorDetails = this.getErrorDetails(error, jsonString)
            
            return {
                isValid: false,
                error: error.message,
                errorType: errorDetails.type,
                line: errorDetails.line,
                column: errorDetails.column,
                message: errorDetails.friendlyMessage,
                rawError: error
            }
        }
    }

    /**
     * Extracts detailed error information from JSON parsing errors
     * @param {Error} error - The error object from JSON.parse
     * @param {string} jsonString - The original JSON string
     * @returns {Object} Detailed error information
     */
    static getErrorDetails(error, jsonString) {
        const errorMessage = error.message.toLowerCase()
        
        // Extract position information from error message
        let line = null
        let column = null
        let errorType = 'SYNTAX_ERROR'
        let friendlyMessage = 'Invalid JSON syntax'

        // Try to extract position from error message
        const positionMatch = errorMessage.match(/position (\d+)/i)
        if (positionMatch) {
            const position = parseInt(positionMatch[1])
            const lineInfo = this.getLineAndColumn(jsonString, position)
            line = lineInfo.line
            column = lineInfo.column
        }

        // Categorize common JSON errors
        if (errorMessage.includes('unexpected token')) {
            errorType = 'UNEXPECTED_TOKEN'
            const tokenMatch = errorMessage.match(/unexpected token (.+?) in json/i)
            if (tokenMatch) {
                friendlyMessage = `Unexpected character '${tokenMatch[1]}' found`
            } else {
                friendlyMessage = 'Unexpected character found'
            }
        } else if (errorMessage.includes('unexpected end')) {
            errorType = 'UNEXPECTED_END'
            friendlyMessage = 'Unexpected end of JSON input - missing closing bracket or quote'
        } else if (errorMessage.includes('expected')) {
            // Check if it's actually a missing comma case (common pattern)
            if (errorMessage.includes('expected') && (jsonString.includes('" "') || jsonString.includes('} {') || /\d\s+\d/.test(jsonString))) {
                errorType = 'UNEXPECTED_TOKEN'
                friendlyMessage = 'Missing comma between elements'
            } else {
                errorType = 'EXPECTED_TOKEN'
                friendlyMessage = 'Expected a different character or token'
            }
        } else if (errorMessage.includes('duplicate')) {
            errorType = 'DUPLICATE_KEY'
            friendlyMessage = 'Duplicate key found in object'
        }

        // Add position information to friendly message if available
        if (line !== null && column !== null) {
            friendlyMessage += ` at line ${line}, column ${column}`
        }

        return {
            type: errorType,
            line,
            column,
            friendlyMessage,
            originalMessage: error.message
        }
    }

    /**
     * Converts character position to line and column numbers
     * @param {string} text - The text to analyze
     * @param {number} position - Character position
     * @returns {Object} Line and column information
     */
    static getLineAndColumn(text, position) {
        const lines = text.substring(0, position).split('\n')
        return {
            line: lines.length,
            column: lines[lines.length - 1].length + 1
        }
    }

    /**
     * Quick validation check - returns boolean only
     * @param {string} jsonString - The JSON string to validate
     * @returns {boolean} True if valid JSON, false otherwise
     */
    static isValid(jsonString) {
        const result = this.validate(jsonString)
        return result.isValid
    }

    /**
     * Real-time validation for input fields
     * Provides debounced validation suitable for real-time feedback
     * @param {string} jsonString - The JSON string to validate
     * @param {Function} callback - Callback function to handle validation result
     * @param {number} delay - Debounce delay in milliseconds (default: 300)
     * @returns {Function} Cleanup function to cancel pending validation
     */
    static validateRealTime(jsonString, callback, delay = 300) {
        // Clear any existing timeout
        if (this._validationTimeout) {
            clearTimeout(this._validationTimeout)
        }

        // Set new timeout for debounced validation
        this._validationTimeout = setTimeout(() => {
            const result = this.validate(jsonString)
            callback(result)
        }, delay)

        // Return cleanup function
        return () => {
            if (this._validationTimeout) {
                clearTimeout(this._validationTimeout)
                this._validationTimeout = null
            }
        }
    }

    /**
     * Validates and provides suggestions for common JSON mistakes
     * @param {string} jsonString - The JSON string to validate
     * @returns {Object} Validation result with suggestions
     */
    static validateWithSuggestions(jsonString) {
        const result = this.validate(jsonString)
        
        if (!result.isValid) {
            result.suggestions = this.getSuggestions(jsonString, result)
        }
        
        return result
    }

    /**
     * Provides suggestions for fixing common JSON errors
     * @param {string} jsonString - The original JSON string
     * @param {Object} validationResult - The validation result
     * @returns {Array} Array of suggestion strings
     */
    static getSuggestions(jsonString, validationResult) {
        const suggestions = []
        const errorType = validationResult.errorType
        
        switch (errorType) {
            case 'UNEXPECTED_TOKEN':
                suggestions.push('Check for missing quotes around string values')
                suggestions.push('Ensure all object keys are quoted')
                suggestions.push('Verify that commas separate array elements and object properties')
                break
                
            case 'UNEXPECTED_END':
                suggestions.push('Check for missing closing brackets ] or }')
                suggestions.push('Ensure all strings are properly closed with quotes')
                suggestions.push('Verify that the JSON structure is complete')
                break
                
            case 'EXPECTED_TOKEN':
                suggestions.push('Check for missing commas between elements')
                suggestions.push('Ensure proper nesting of objects and arrays')
                break
                
            case 'DUPLICATE_KEY':
                suggestions.push('Remove duplicate keys from objects')
                suggestions.push('Each key in an object must be unique')
                break
                
            default:
                suggestions.push('Check JSON syntax and structure')
                suggestions.push('Use a JSON formatter to identify issues')
        }
        
        return suggestions
    }
}

// Static property to hold validation timeout
JSONValidator._validationTimeout = null